"use client"

import DashboardContentWrapper from "../../../../../shared/components/dashboard-content-wrapper"
import Heading from "../../../../../shared/components/ui/typography/heading"
import {
    getWorkOrderById,
    updateWorkOrderTrainingLinks,
    updateWorkOrder,
    createWorkOrder,
    updateWorkOrderStatus,
} from "@/features/work-orders/api/work-orders.api"
import { ServerActionResponse } from "@/shared/types"
import { updateHome } from "@/features/homeowners/api/homes.api"
import { getHome } from "@/features/homeowners/api/homeowners.api"
import { notFound } from "next/navigation"
import Link from "next/link"
import { useState, useEffect, useRef } from "react"
import {
    ChevronLeft,
    ChevronRight,
    User,
    Users,
    UserCheck,
    Plus,
    ExternalLink,
    X,
    Loader2,
    PlusIcon,
    Pencil,
    Copy,
    Camera,
} from "lucide-react"
import { useSession } from "@/features/auth/context/session.context"
import { authFetchJson, authFetch } from "@/shared/lib/auth-fetch"
import { UserRole } from "@/shared/constants/user-roles"
import { cn, buildRegionAwareHref } from "@/shared/lib/utils"
import { buildGoogleMapsPlaceUrl } from "@/shared/lib/utils/google-maps-url"
import {
  shouldMaskHomeownerData,
  maskHomeownerLastName,
  maskHomeownerPhone,
  maskHomeownerAddress,
} from "@/shared/lib/utils/homeowner-privacy.utils"
import PrintButton from "@/features/work-orders/components/print-button"
import ProgressTrackingForm from "@/features/work-orders/components/forms/progress-tracking-form"
import { Button, buttonVariants } from "../../../../../shared/components/ui/button"
import { Input } from "../../../../../shared/components/ui/input"
import { Label } from "../../../../../shared/components/ui/label"
import { Textarea } from "../../../../../shared/components/ui/textarea"
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
} from "../../../../../shared/components/ui/dialog"
import { useRouter, useSearchParams, useParams } from "next/navigation"
import { WorkOrderHoursDatepicker, formatDateForDB } from "@/features/work-orders/components/work-order-hours-datepicker"
import { MultiSelectWithFilters, FilterableOption } from "../../../../../shared/components/ui/multi-select-with-filters"
import StickySaveBar from "../../../../../shared/components/sticky-save-bar"
import QRCode from "react-qr-code"
import { HOMEOWNERS_ROUTES, DASHBOARD_ROUTE } from "../../../../../shared/constants/pages-routes"
import { useRegionAwareHref } from "@/shared/hooks/use-region-aware-href"
import { buildHomeownersDetailUrlWithReturn } from "@/shared/lib/utils/navigation-helpers"
import { useRegion } from "@/shared/hooks/use-region"
import { useRegionId } from "@/shared/hooks/use-region-id"
import successToast from "@/shared/lib/toast/success.toast"
import errorToast from "@/shared/lib/toast/error.toast"
import { useMemo } from "react"
import AddPhotoModal from "@/features/photos/components/add-photo-modal"
import CameraCaptureModal from "@/features/photos/components/camera-capture-modal"
import PostCapturePopup from "@/features/photos/components/post-capture-popup"
import VoiceRecordingModal from "@/features/photos/components/voice-recording-modal"
import TextInputModal from "@/features/photos/components/text-input-modal"
import { uploadMultiplePhotosDirect } from "@/features/photos/upload-client"
import { updatePhoto } from "@/features/photos/api/photos.api"
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../../../../../shared/components/ui/accordion"
import { TOOL_KIT_CONTENTS, TOOL_KIT_NAME, isToolKit, sortToolsWithToolKitFirst } from "@/features/work-orders/constants/tool-kit-contents"
import { formatWorkScope } from "@/features/work-orders/utils/format-work-scope"
import { stripBlankLineItems } from "@/features/work-orders/utils/tools-supplies-blank-lines"
import { usePrintChecklistBlankLines } from "@/features/work-orders/hooks/use-print-checklist-blank-lines"
import ToolSupplyBlankLineRow from "@/features/work-orders/components/tool-supply-blank-line-row"
import ScrollToTopFab from "@/shared/components/scroll-to-top-fab"
import {
    DateRangePickerControl,
    type DateRangePickerValue,
} from "@/shared/components/form-inputs/date-range-picker-control"

interface WorkOrderDetailPageProps {
    params: Promise<{ id: string }>
}

// Helper function to format American phone numbers
const formatPhoneNumber = (phone: string | null | undefined): string => {
    if (!phone) return "No phone"

    // Remove all non-digit characters
    const digits = phone.replace(/\D/g, "")

    // Check if it's an American number (10 digits or 11 digits starting with 1)
    if (digits.length === 11 && digits.startsWith("1")) {
        // Remove the leading 1 and format
        const cleaned = digits.substring(1)
        return `(${cleaned.substring(0, 3)}) ${cleaned.substring(3, 6)}-${cleaned.substring(6)}`
    } else if (digits.length === 10) {
        // Format 10-digit number
        return `(${digits.substring(0, 3)}) ${digits.substring(3, 6)}-${digits.substring(6)}`
    }

    // Return original if not a standard American format
    return phone
}

// Helper for tel: link from phone (matches Homeowners / Work Order cards)
function getPhoneTelLink(phone: string | null | undefined): string | null {
    if (!phone) return null
    return `tel:${phone.replace(/[^\d+]/g, "")}`
}

function WorkOrderDetailPage() {
    const routerParams = useParams() as any
    const [workOrder, setWorkOrder] = useState<any>(null)
    const [volunteers, setVolunteers] = useState<any[]>([])
    const [allVolunteers, setAllVolunteers] = useState<any[]>([])
    const [loading, setLoading] = useState(true)
    const [isAddLinkModalOpen, setIsAddLinkModalOpen] = useState(false)
    const [newLinkInput, setNewLinkInput] = useState("")
    const [workOrderId, setWorkOrderId] = useState<number | null>(null)
    const [deletingLink, setDeletingLink] = useState<string | null>(null)
    
    // Work Order Hours state
    const [workOrderHours, setWorkOrderHours] = useState<Array<{ date: string | null; hoursWorked: number }>>([])
    const [originalWorkOrderHours, setOriginalWorkOrderHours] = useState<Array<{ date: string | null; hoursWorked: number }>>([])
    const [addWorkDaysRange, setAddWorkDaysRange] = useState<DateRangePickerValue>({})
    
    // Volunteers state
    const [selectedVolunteerIds, setSelectedVolunteerIds] = useState<string[]>([])
    const [originalVolunteerIds, setOriginalVolunteerIds] = useState<string[]>([])
    
    // Tools and Supplies state
    // Tools and supplies now include status in the object
    type ToolSupplyItem = { name: string; out: boolean; in: boolean }
    const [toolsNeeded, setToolsNeeded] = useState<ToolSupplyItem[]>([])
    const [originalToolsNeeded, setOriginalToolsNeeded] = useState<ToolSupplyItem[]>([])
    const [suppliesNeeded, setSuppliesNeeded] = useState<ToolSupplyItem[]>([])
    const [originalSuppliesNeeded, setOriginalSuppliesNeeded] = useState<ToolSupplyItem[]>([])
    
    // Progress state
    const [progress, setProgress] = useState<number>(0)
    const [originalProgress, setOriginalProgress] = useState<number>(0)
    
    const [isSaving, setIsSaving] = useState(false)
    const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false)
    const [showSuccessBar, setShowSuccessBar] = useState(false)
    const [isAddItemModalOpen, setIsAddItemModalOpen] = useState(false)
    const [newItemInput, setNewItemInput] = useState("")
    const [itemType, setItemType] = useState<"tool" | "supply">("tool")
    const [isAddPhotoModalOpen, setIsAddPhotoModalOpen] = useState(false)
    
    // Camera capture flow state (Take Photo - match HD Photos)
    interface CapturedPhoto {
        file: File
        url: string
        thumbnailUrl: string | null
        description?: string | null
        takenAt: string
        tempId: string
        uploadedPhotoId?: number
    }
    const [isCameraOpen, setIsCameraOpen] = useState(false)
    const [capturedPhotos, setCapturedPhotos] = useState<CapturedPhoto[]>([])
    const [showPostCapturePopup, setShowPostCapturePopup] = useState(false)
    const [currentPhotoTempId, setCurrentPhotoTempId] = useState<string | null>(null)
    const [lastSavedThumbnailUrl, setLastSavedThumbnailUrl] = useState<string | null>(null)
    const [isVoiceRecordingOpen, setIsVoiceRecordingOpen] = useState(false)
    const [isTextInputOpen, setIsTextInputOpen] = useState(false)
    const [pendingTranscriptions, setPendingTranscriptions] = useState<Set<string>>(new Set())
    const [uploadingPhotos, setUploadingPhotos] = useState<Set<string>>(new Set())
    const isTextInputSavingRef = useRef(false)
    const isCameraOpenRef = useRef(isCameraOpen)
    const isTextInputOpenRef = useRef(isTextInputOpen)
    const isVoiceRecordingOpenRef = useRef(isVoiceRecordingOpen)
    const currentTakenAtRef = useRef<string>(new Date().toISOString())
    
    useEffect(() => { isCameraOpenRef.current = isCameraOpen }, [isCameraOpen])
    useEffect(() => { isTextInputOpenRef.current = isTextInputOpen }, [isTextInputOpen])
    useEffect(() => { isVoiceRecordingOpenRef.current = isVoiceRecordingOpen }, [isVoiceRecordingOpen])
    
    // Notes/Status/Next Steps state
    const [workOrderNote, setWorkOrderNote] = useState("")
    const [originalWorkOrderNote, setOriginalWorkOrderNote] = useState("")
    const [accessDenied, setAccessDenied] = useState(false)

    // Get current user information
    const { profile, session} = useSession()
    const router = useRouter()
    const searchParams = useSearchParams()
    const autoPrintTriggeredRef = useRef(false)
    
    // All hooks must be called at the top level, before any conditional logic
    // Call useRegionAwareHref unconditionally - we'll compute the value to pass using useMemo
    const homeDetailsBaseUrl = useMemo(() => {
        if (!workOrder?.home?.homeowner?.id) return ''
        const baseUrl = buildHomeownersDetailUrlWithReturn(workOrder.home.homeowner.id)
        // Add tab parameter
        const separator = baseUrl.includes('?') ? '&' : '?'
        return `${baseUrl}${separator}tab=home`
    }, [workOrder?.home?.homeowner?.id])
    const homeDetailsHref = useRegionAwareHref(homeDetailsBaseUrl)

    // Check if current user is a volunteer or team leader
    const isVolunteer = session?.role === UserRole.VOLUNTEER
    const isTeamLeader = session?.role === UserRole.TEAM_LEADER
    const isBaseLeader = session?.role === UserRole.BASE_LEADER
    const isAdmin = session?.role === UserRole.ADMIN
    const canAddNotes = isVolunteer || isTeamLeader || isBaseLeader || isAdmin
    const canManageWorkOrders = isBaseLeader || isAdmin

    // "Own" WO: Volunteer assigned to this WO, or Homeowner's home. Don't mask homeowner data in those cases.
    const currentUserId = profile?.id ?? session?.sub
    const volunteerIds = (workOrder as any)?.volunteerIds ?? []
    const isAssignedVolunteer = isVolunteer && volunteerIds.some((id: number) => String(id) === String(currentUserId))
    const homeownerUserId = workOrder?.home?.homeowner?.user?.id
    const isHomeownerOwnHome = session?.role === UserRole.HOMEOWNER && currentUserId != null && homeownerUserId != null && String(currentUserId) === String(homeownerUserId)
    const isOwnWo = isAssignedVolunteer || isHomeownerOwnHome
    const shouldMaskOwner = !isOwnWo && shouldMaskHomeownerData(
      session?.role,
      session?.sub,
      homeownerUserId
    )

    const { regionSlug } = useRegion()
    const { regionId } = useRegionId()
    const [isDuplicating, setIsDuplicating] = useState(false)
    const [isUpdatingStatus, setIsUpdatingStatus] = useState(false)

    // Format date stamp for notes (MM/DD/YY UserName:)
    const formatDateStamp = () => {
        const now = new Date()
        const month = String(now.getMonth() + 1).padStart(2, '0')
        const day = String(now.getDate()).padStart(2, '0')
        const year = String(now.getFullYear()).slice(-2)
        const userName = profile?.fullname || 'Unknown User'
        return `${month}/${day}/${year} ${userName}:`
    }


    // Always sort tools so Tool Kit is first for display; strip legacy blank lines
    const sortedToolsNeeded = useMemo(() => {
        return stripBlankLineItems(
            sortToolsWithToolKitFirst(toolsNeeded || []) as ToolSupplyItem[]
        )
    }, [toolsNeeded])

    const displaySuppliesNeeded = useMemo(() => {
        return stripBlankLineItems(suppliesNeeded || [])
    }, [suppliesNeeded])

    const {
        checklistSectionRef,
        toolsColumnRef,
        suppliesColumnRef,
        toolsItemsRef,
        suppliesItemsRef,
        rowHeightMeasureRef,
        printBlankLines,
        prepareForPrint,
    } = usePrintChecklistBlankLines(
        sortedToolsNeeded.length,
        displaySuppliesNeeded.length
    )

    useEffect(() => {
        if (
            autoPrintTriggeredRef.current ||
            loading ||
            !workOrder ||
            searchParams.get("print") !== "true"
        ) {
            return
        }

        autoPrintTriggeredRef.current = true
        window.scrollTo(0, 0)
        window.setTimeout(() => {
            prepareForPrint()
            window.print()
        }, 150)
    }, [loading, searchParams, workOrder, prepareForPrint])

    // Extract unique organizations from volunteers for filter dropdown
    const organizationOptions = useMemo(() => {
        const orgs = new Set<string>()
        allVolunteers.forEach((volunteer) => {
            if (volunteer.user.organizationName) {
                orgs.add(volunteer.user.organizationName)
            }
        })
        return Array.from(orgs).sort()
    }, [allVolunteers])

    // Map volunteers to filterable options format
    const volunteerOptions = useMemo(() => {
        return allVolunteers.map((volunteer) => ({
            id: volunteer.id.toString(),
            name: volunteer.user.fullname,
            role: volunteer.user.role?.name || "Unknown",
            organization: volunteer.user.organizationName || undefined,
            // Use availability dates as proxy for serving dates until volunteer dates are available
            arrivalDate: volunteer.user.availabilityStart ? new Date(volunteer.user.availabilityStart).toISOString().split('T')[0] : undefined,
            departureDate: volunteer.user.availabilityEnd ? new Date(volunteer.user.availabilityEnd).toISOString().split('T')[0] : undefined,
        }))
    }, [allVolunteers])

    // Detect changes in any editable field
    useEffect(() => {
        const hoursChanged = JSON.stringify(workOrderHours) !== JSON.stringify(originalWorkOrderHours)
        const volunteersChanged = JSON.stringify(selectedVolunteerIds.sort()) !== JSON.stringify(originalVolunteerIds.sort())
        const sortedCurrentTools = sortToolsWithToolKitFirst(stripBlankLineItems([...toolsNeeded]))
        const sortedOriginalTools = sortToolsWithToolKitFirst(stripBlankLineItems([...originalToolsNeeded]))
        const toolsChanged = JSON.stringify(sortedCurrentTools) !== JSON.stringify(sortedOriginalTools)
        const suppliesChanged = JSON.stringify(stripBlankLineItems([...suppliesNeeded])) !== JSON.stringify(stripBlankLineItems([...originalSuppliesNeeded]))
        const progressChanged = progress !== originalProgress
        const noteChanged = workOrderNote.trim() !== originalWorkOrderNote.trim()
        
        const hasChanges = hoursChanged || volunteersChanged || toolsChanged || suppliesChanged || progressChanged || noteChanged
        setHasUnsavedChanges(hasChanges)
    }, [workOrderHours, originalWorkOrderHours, selectedVolunteerIds, originalVolunteerIds, toolsNeeded, originalToolsNeeded, suppliesNeeded, originalSuppliesNeeded, progress, originalProgress, workOrderNote, originalWorkOrderNote])

    // Helper to check if a specific field is modified
    const isFieldModified = (index: number, field: 'date' | 'hoursWorked'): boolean => {
        if (!originalWorkOrderHours[index]) return true
        return workOrderHours[index]?.[field] !== originalWorkOrderHours[index]?.[field]
    }

    // Helper to check if volunteers are modified
    const isVolunteersModified = (): boolean => {
        return JSON.stringify(selectedVolunteerIds.sort()) !== JSON.stringify(originalVolunteerIds.sort())
    }

    // Helper to check if tools are modified
    const isToolsModified = (): boolean => {
        const sortedCurrent = sortToolsWithToolKitFirst(stripBlankLineItems([...toolsNeeded]))
        const sortedOriginal = sortToolsWithToolKitFirst(stripBlankLineItems([...originalToolsNeeded]))
        return JSON.stringify(sortedCurrent) !== JSON.stringify(sortedOriginal)
    }

    // Helper to check if supplies are modified
    const isSuppliesModified = (): boolean => {
        const sortedCurrent = stripBlankLineItems([...suppliesNeeded]).sort((a, b) => a.name.localeCompare(b.name))
        const sortedOriginal = stripBlankLineItems([...originalSuppliesNeeded]).sort((a, b) => a.name.localeCompare(b.name))
        return JSON.stringify(sortedCurrent) !== JSON.stringify(sortedOriginal)
    }

    // Helper to check if progress is modified
    const isProgressModified = (): boolean => {
        return progress !== originalProgress
    }

    // Helper to check if a date is required (has hours but no date)
    const isDateRequired = (index: number): boolean => {
        const entry = workOrderHours[index]
        return !!(entry && entry.hoursWorked && entry.hoursWorked > 0 && !entry.date)
    }

    const getDatesInRange = (from: Date, to: Date): Date[] => {
        const start = new Date(from.getFullYear(), from.getMonth(), from.getDate())
        const end = new Date(to.getFullYear(), to.getMonth(), to.getDate())
        const dates: Date[] = []
        const current = new Date(start)

        while (current <= end) {
            dates.push(new Date(current))
            current.setDate(current.getDate() + 1)
        }

        return dates
    }

    const addWorkDaysFromRange = (range: DateRangePickerValue) => {
        if (!range.from || !range.to) return

        const existingRows = workOrderHours.filter(
            (entry) => entry.date || entry.hoursWorked > 0
        )
        const existingDateKeys = new Set(
            existingRows
                .filter((entry) => entry.date)
                .map((entry) => entry.date as string)
        )
        const newRows = getDatesInRange(range.from, range.to)
            .map((date) => formatDateForDB(date))
            .filter((date) => !existingDateKeys.has(date))
            .map((date) => ({ date, hoursWorked: 0 }))

        const nextRows =
            existingRows.length || newRows.length
                ? [...existingRows, ...newRows]
                : [{ date: null, hoursWorked: 0 }]

        setWorkOrderHours(nextRows)
        setAddWorkDaysRange({})
    }


    useEffect(() => {
        const fetchData = async () => {
            try {
                const id = routerParams.id
                if (!id) return
                const parsedId = parseInt(id)

                if (isNaN(parsedId)) {
                    notFound()
                    return
                }

                setWorkOrderId(parsedId)

                const workOrderResponse = await getWorkOrderById(parsedId)

                if (!workOrderResponse.success || !workOrderResponse.data) {
                    const status = (workOrderResponse as { status?: number }).status
                    if (status === 403) {
                        setAccessDenied(true)
                        setLoading(false)
                        return
                    }
                    notFound()
                    return
                }

                const workOrderData = workOrderResponse.data
                setWorkOrder(workOrderData)

                // Debug: Log the work order data to see what we're getting
                console.log('Work Order Data:', workOrderData)
                console.log('workOrderHours field:', (workOrderData as any).workOrderHours)
                console.log('workOrderHours type:', typeof (workOrderData as any).workOrderHours)

                // Initialize workOrderHours from the work order data
                let initialHours
                let rawWorkOrderHours = (workOrderData as any).workOrderHours

                // Parse if it's a string (TypeORM might return JSONB as string)
                if (typeof rawWorkOrderHours === 'string') {
                    try {
                        rawWorkOrderHours = JSON.parse(rawWorkOrderHours)
                        console.log('Parsed workOrderHours from string:', rawWorkOrderHours)
                    } catch (e) {
                        console.error('Failed to parse workOrderHours:', e)
                        rawWorkOrderHours = null
                    }
                }

                if (rawWorkOrderHours && Array.isArray(rawWorkOrderHours) && rawWorkOrderHours.length > 0) {
                    // Use existing workOrderHours from database
                    initialHours = rawWorkOrderHours
                    console.log('Using existing workOrderHours:', initialHours)
                } else {
                    // Fallback: create one empty entry
                    initialHours = [{ date: null, hoursWorked: 0 }]
                    console.log('No workOrderHours found, using default empty entry')
                }
                
                setWorkOrderHours(initialHours)
                setOriginalWorkOrderHours(JSON.parse(JSON.stringify(initialHours))) // Deep copy

                // Initialize tools and supplies - now they include status in the object
                const initialTools = (workOrderData.toolsNeeded || []).map((item: any) => {
                    // Handle migration: if it's still a string, convert to object
                    if (typeof item === 'string') {
                        return { name: item, out: false, in: false }
                    }
                    // Ensure all required fields exist
                    return {
                        name: item.name || item,
                        out: item.out || false,
                        in: item.in || false
                    }
                })
                const initialSupplies = (workOrderData.suppliesNeeded || []).map((item: any) => {
                    // Handle migration: if it's still a string, convert to object
                    if (typeof item === 'string') {
                        return { name: item, out: false, in: false }
                    }
                    // Ensure all required fields exist
                    return {
                        name: item.name || item,
                        out: item.out || false,
                        in: item.in || false
                    }
                })
                // Sort tools so Tool Kit is always first; strip legacy blank lines from saved data
                const sortedTools = sortToolsWithToolKitFirst(stripBlankLineItems(initialTools))
                setToolsNeeded([...sortedTools])
                setOriginalToolsNeeded([...sortedTools])
                const strippedSupplies = stripBlankLineItems(initialSupplies)
                setSuppliesNeeded([...strippedSupplies])
                setOriginalSuppliesNeeded([...strippedSupplies])

                // Initialize progress
                const initialProgress = workOrderData.progress || 0
                setProgress(initialProgress)
                setOriginalProgress(initialProgress)

                // Use volunteerIds as the authoritative source; fall back to volunteers relation only.
                let userIds: number[] = []
                if (Array.isArray(workOrderData.volunteerIds)) {
                    userIds = workOrderData.volunteerIds
                        .map((id: unknown) =>
                            typeof id === "number"
                                ? id
                                : typeof id === "string"
                                ? parseInt(id, 10)
                                : NaN
                        )
                        .filter((id: number) => !isNaN(id))
                } else if (Array.isArray((workOrderData as any).volunteers)) {
                    userIds = (workOrderData as any).volunteers
                        .map((volunteer: any) => volunteer?.id)
                        .filter((id: unknown) => typeof id === "number")
                } else {
                    console.warn(
                        "Work order missing volunteerIds; volunteers list may be incomplete.",
                        workOrderData.id
                    )
                }

                // Fetch all volunteers/team leaders
                const volunteersData: any[] = []
                const allVolunteersData: any[] = []
                    try {
                        const data = await authFetchJson<any[]>('/user/by-role?roleId=1,2,3,4')
                        if (data.success && Array.isArray(data.data)) {
                            // Store all volunteers for the multiselect
                            allVolunteersData.push(...data.data.map((user: any) => ({
                                id: user.id,
                                user: {
                                    id: user.id,
                                    fullname: user.fullname,
                                    email: user.email,
                                    phone: user.phone,
                                    role: user.role || { id: 0, name: 'Unknown' },
                                    organizationName: user.organizationName || null,
                                    // Note: arrivalDate/departureDate would come from volunteer entity if available
                                    // For now, using availability dates as fallback
                                    availabilityStart: user.availabilityStart || null,
                                    availabilityEnd: user.availabilityEnd || null
                                }
                            })))
                            
                            // Filter to only the users in our team for display
                            if (userIds.length > 0) {
                                const teamMembers = data.data.filter((user: any) => 
                                    userIds.includes(user.id)
                                )
                                volunteersData.push(...teamMembers.map((user: any) => ({
                                    id: user.id,
                                    user: {
                                        id: user.id,
                                        fullname: user.fullname,
                                        email: user.email,
                                        phone: user.phone,
                                        role: user.role || { id: 0, name: 'Unknown' }
                                    }
                                })))
                            }
                        }
                    } catch (error) {
                        console.error("Error fetching volunteers:", error)
                }
                setVolunteers(volunteersData)
                setAllVolunteers(allVolunteersData)
                const volunteerIdStrings = userIds.map(id => id.toString())
                setSelectedVolunteerIds(volunteerIdStrings)
                setOriginalVolunteerIds([...volunteerIdStrings])

                console.log("workOrder:::", workOrderData)
                console.log("volunteers:::", volunteersData)
            } catch (error) {
                console.error("Error fetching work order data:", error)
            } finally {
                setLoading(false)
            }
        }

        fetchData()
    }, [routerParams.id])

    const workOrderRegionId =
        (workOrder as any)?.home?.regionId ??
        (workOrder as any)?.home?.region?.id ??
        null
    const isRegionMismatch =
        regionId != null &&
        workOrderRegionId != null &&
        Number(workOrderRegionId) !== Number(regionId)

    useEffect(() => {
        if (!workOrder || !isRegionMismatch) return
        errorToast("This work order is not in that region.")
        router.replace(buildRegionAwareHref(regionSlug, DASHBOARD_ROUTE.workOrders))
    }, [workOrder, isRegionMismatch, router, regionSlug])

    if (loading) {
        return (
            <DashboardContentWrapper className="space-y-6 pb-24 md:pb-6">
                <div className="flex items-center justify-center py-12">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
                </div>
            </DashboardContentWrapper>
        )
    }

    if (accessDenied) {
        return (
            <DashboardContentWrapper className="space-y-6 pb-24 md:pb-6">
                <div className="flex flex-col items-center justify-center py-12 text-center max-w-md mx-auto">
                    <p className="text-muted-foreground mb-4">
                        You do not have access to this work order.
                    </p>
                    <Button asChild>
                        <Link href={buildRegionAwareHref(regionSlug, DASHBOARD_ROUTE.workOrders)}>
                            Back to Work Orders
                        </Link>
                    </Button>
                </div>
            </DashboardContentWrapper>
        )
    }

    if (isRegionMismatch) {
        return (
            <DashboardContentWrapper className="space-y-6 pb-24 md:pb-6">
                <div className="flex items-center justify-center py-12">
                    <p className="text-muted-foreground">Redirecting...</p>
                </div>
            </DashboardContentWrapper>
        )
    }

    if (!workOrder) {
        notFound()
        return null
    }

    // Helper function to format hours worked
    const formatHoursWorked = (hours: number | null | undefined): string => {
        if (hours === null || hours === undefined || isNaN(hours)) {
            return "N/A"
        }

        return `${hours}h 00m`
    }

    // Duplicate work order (BaseLeader/Admin only)
    const handleDuplicate = async () => {
        if (!workOrder?.home?.id || isDuplicating) return
        setIsDuplicating(true)
        try {
            const toolsNeeded = stripBlankLineItems(
                Array.isArray(workOrder.toolsNeeded)
                ? workOrder.toolsNeeded.map((item: any) =>
                      typeof item === "object" && item != null && "name" in item
                          ? { name: item.name ?? item, out: item.out ?? false, in: item.in ?? false }
                          : { name: String(item ?? ""), out: false, in: false }
                  )
                : []
            )
            const suppliesNeeded = stripBlankLineItems(
                Array.isArray(workOrder.suppliesNeeded)
                ? workOrder.suppliesNeeded.map((item: any) =>
                      typeof item === "object" && item != null && "name" in item
                          ? { name: item.name ?? item, out: item.out ?? false, in: item.in ?? false }
                          : { name: String(item ?? ""), out: false, in: false }
                  )
                : []
            )

            const duplicatePayload = {
                workOrderType: workOrder.workOrderType ?? "",
                workScopeId: workOrder.workScopeId ?? workOrder.workScope?.id ?? null,
                homeId: workOrder.home.id,
                ownerId: workOrder.owner?.id ?? 0,
                teamName: "",
                teamLeadId: 0,
                volunteersNeeded: workOrder.volunteersNeeded,
                projectedVolunteerHours: workOrder.projectedVolunteerHours,
                projectDescription: workOrder.projectDescription ?? "",
                notes: workOrder.notes ?? "",
                volunteerIds: [],
                toolsNeeded,
                suppliesNeeded,
                workOrderHours: [],
            }

            const response = await createWorkOrder(duplicatePayload as any)
            if (response.success && response.data) {
                const newId = (response.data as any)?.id
                if (newId != null) {
                    successToast("Work order duplicated. Edit the new copy to assign team and work days.")
                    router.push(buildRegionAwareHref(regionSlug, `/work-orders/edit/${newId}`))
                    return
                }
            }
            errorToast((response as any)?.error ?? "Failed to duplicate work order.")
        } catch (e) {
            errorToast(e instanceof Error ? e.message : "Failed to duplicate work order.")
        } finally {
            setIsDuplicating(false)
        }
    }

    // Close/Reopen work order (Active/Inactive) - Base Leader and Admin only
    const handleStatusChange = async (checked: boolean) => {
        if (!workOrderId || !workOrder) return
        const newStatus = checked ? "Active" : "Inactive"
        const previousStatus = workOrder.status
        setWorkOrder((prev: any) => (prev ? { ...prev, status: newStatus } : null))
        setIsUpdatingStatus(true)
        try {
            const result = await updateWorkOrderStatus(workOrderId, newStatus)
            if (result.success) {
                successToast(newStatus === "Inactive" ? "Work order status updated to Closed" : "Work order status updated to Open")
            } else {
                setWorkOrder((prev: any) => (prev ? { ...prev, status: previousStatus } : null))
                errorToast((result as any)?.error ?? "Failed to update work order status.")
            }
        } catch (e) {
            setWorkOrder((prev: any) => (prev ? { ...prev, status: previousStatus } : null))
            errorToast(e instanceof Error ? e.message : "Failed to update work order status.")
        } finally {
            setIsUpdatingStatus(false)
        }
    }

    // Function to handle adding a new training link
    const handleAddTrainingLink = async () => {
        if (!newLinkInput.trim()) {
            alert("Please enter a link")
            return
        }

        if (!workOrderId) {
            alert("Work order ID not found")
            return
        }

        const newLink = newLinkInput.trim()

        // Get current training links and add the new one
        const currentLinks = workOrder?.trainingLinks || []
        const updatedLinks = [...currentLinks, newLink]

        // Close modal and reset input immediately
        setIsAddLinkModalOpen(false)
        setNewLinkInput("")

        try {
            // Update the work order in the database
            const response = await updateWorkOrderTrainingLinks(workOrderId, {
                trainingLinks: updatedLinks,
            })

            if (response.success) {
                // Update the local state with the new link
                setWorkOrder((prev: any) => ({
                    ...prev,
                    trainingLinks: updatedLinks,
                }))
            } else {
                console.error("Failed to add training link:", response.message)
                alert("Failed to add training link. Please try again.")
            }
        } catch (error) {
            console.error("Error adding training link:", error)
            alert(
                "An error occurred while adding the training link. Please try again."
            )
        }
    }

    // Function to handle removing a training link
    const handleRemoveTrainingLink = async (linkToRemove: string) => {
        if (!workOrderId) {
            alert("Work order ID not found")
            return
        }

        if (deletingLink) return // Prevent multiple deletions

        setDeletingLink(linkToRemove)

        try {
            // Get current training links and remove the specified one
            const currentLinks = workOrder?.trainingLinks || []
            const updatedLinks = currentLinks.filter(
                (link: string) => link !== linkToRemove
            )

            // Update the work order in the database
            const response = await updateWorkOrderTrainingLinks(workOrderId, {
                trainingLinks: updatedLinks,
            })

            if (response.success) {
                // Update the local state
                setWorkOrder((prev: any) => ({
                    ...prev,
                    trainingLinks: updatedLinks,
                }))
            } else {
                console.error("Failed to remove training link:", response.message)
                alert("Failed to remove training link. Please try again.")
            }
        } catch (error) {
            console.error("Error removing training link:", error)
            alert(
                "An error occurred while removing the training link. Please try again."
            )
        } finally {
            setDeletingLink(null)
        }
    }

    // Consolidated function to handle saving all changes
    const handleSaveAllChanges = async () => {
        if (!workOrderId) {
            console.error("Work order ID not found")
            return
        }

        if (isSaving) return // Prevent multiple saves

        setIsSaving(true)

        try {
            // Prepare the update data object
            const updateData: any = {}

            // Handle work order hours if changed
            if (JSON.stringify(workOrderHours) !== JSON.stringify(originalWorkOrderHours)) {
                // Filter out entries that have neither date nor hours
                const nonEmptyHours = workOrderHours.filter(
                    entry => entry.date || (entry.hoursWorked && entry.hoursWorked > 0)
                )

                // Validation: Check if any remaining entry has hours but no date
                const hasHoursWithoutDate = nonEmptyHours.some(
                    entry => (entry.hoursWorked && entry.hoursWorked > 0) && !entry.date
                )

                if (hasHoursWithoutDate) {
                    alert("Please select a date for all work days that have hours entered.")
                    setIsSaving(false)
                    return
                }

                // Sort work order hours by date (chronological order, null dates at the end)
                const sortedHours = [...nonEmptyHours].sort((a, b) => {
                    // Put null dates at the end
                    if (!a.date && !b.date) return 0
                    if (!a.date) return 1
                    if (!b.date) return -1
                    
                    // Compare dates chronologically
                    return new Date(a.date).getTime() - new Date(b.date).getTime()
                })

                // Format dates to YYYY-MM-DD for database
                const formattedHours = sortedHours.map(entry => ({
                    date: entry.date,
                    hoursWorked: entry.hoursWorked || 0,
                }))

                updateData.workOrderHours = formattedHours
            }

            // Handle volunteers if changed
            if (isVolunteersModified()) {
                const teamIds = selectedVolunteerIds
                    .map((id) => parseInt(id, 10))
                    .filter((id) => !isNaN(id))
                updateData.volunteerIds = teamIds
            }

            // Handle tools if changed
            if (isToolsModified()) {
                updateData.toolsNeeded = sortToolsWithToolKitFirst(
                    stripBlankLineItems(toolsNeeded)
                )
            }

            // Handle supplies if changed
            if (isSuppliesModified()) {
                updateData.suppliesNeeded = stripBlankLineItems(suppliesNeeded)
            }

            // Handle progress if changed
            if (isProgressModified()) {
                updateData.progress = progress
            }

            // Handle note if changed - save to Project General Notes
            const noteChanged = workOrderNote.trim() !== originalWorkOrderNote.trim()
            if (noteChanged && workOrderNote.trim() && workOrder?.home?.id) {
                try {
                    // Fetch current home details to get existing projectGeneralNotes
                    const homeResponse = await getHome(workOrder.home.id)
                    
                    if (homeResponse.success && homeResponse.data) {
                        const currentNotes = homeResponse.data.projectGeneralNotes || ""
                        const dateStamp = formatDateStamp()
                        const newNoteWithTimestamp = `${dateStamp} ${workOrderNote.trim()}`
                        
                        // Append new note to existing notes
                        const updatedNotes = currentNotes 
                            ? `${currentNotes}\n${newNoteWithTimestamp}`
                            : newNoteWithTimestamp

                        // Update home with appended notes
                        await updateHome(workOrder.home.id, {
                            projectGeneralNotes: updatedNotes
                        })
                    }
                } catch (error) {
                    console.error("Error saving note to Project General Notes:", error)
                    // Continue with work order save even if note save fails
                }
            }

            // Only make API call if there are actual changes to the work order
            if (Object.keys(updateData).length === 0 && !noteChanged) {
                setIsSaving(false)
                return
            }

            // Update the work order using the action (only if there are work order changes)
            let response: any = { success: true, data: workOrder }
            if (Object.keys(updateData).length > 0) {
                response = await updateWorkOrder(workOrderId, updateData)
            }

            if (response.success) {
                // Update all original state to match current state
                if (updateData.workOrderHours) {
                    const finalHours = updateData.workOrderHours.length > 0 ? updateData.workOrderHours : [{ date: null, hoursWorked: 0 }]
                    setWorkOrderHours(finalHours)
                    setOriginalWorkOrderHours(JSON.parse(JSON.stringify(finalHours)))
                }

                if (updateData.volunteerIds) {
                    setOriginalVolunteerIds([...selectedVolunteerIds])
                    // Update volunteers display list
                    const updatedVolunteers = allVolunteers.filter(v => 
                        selectedVolunteerIds.includes(v.id.toString())
                    )
                    setVolunteers(updatedVolunteers)
                }

                if (updateData.toolsNeeded) {
                    // Use the sorted tools that were saved, ensure they're still sorted
                    const savedTools = sortToolsWithToolKitFirst(updateData.toolsNeeded) as ToolSupplyItem[]
                    setToolsNeeded([...savedTools])
                    setOriginalToolsNeeded([...savedTools])
                    // Also update the workOrder state if response has data
                    if (response.data) {
                        setWorkOrder((prev: any) => ({
                            ...prev,
                            toolsNeeded: savedTools
                        }))
                    }
                }

                if (updateData.suppliesNeeded) {
                    setOriginalSuppliesNeeded([...suppliesNeeded])
                }

                if (updateData.progress !== undefined) {
                    setOriginalProgress(progress)
                }

                // Handle note if it was changed
                if (noteChanged && workOrderNote.trim()) {
                    // Clear the note input after saving and reset original to empty
                    setWorkOrderNote("")
                    setOriginalWorkOrderNote("")
                }

                // Update the work order state
                setWorkOrder((prev: any) => ({
                    ...prev,
                    ...updateData,
                }))

                setHasUnsavedChanges(false)
                
                // Show success bar
                setShowSuccessBar(true)
                setTimeout(() => {
                    setShowSuccessBar(false)
                }, 3000)
            } else {
                console.error("Failed to save changes:", response.message)
                alert(`Failed to save changes: ${response.message}`)
            }
        } catch (error) {
            console.error("Error saving changes:", error)
            alert("An error occurred while saving changes")
        } finally {
            setIsSaving(false)
        }
    }

    // Handle adding new tool or supply
    const handleAddItem = () => {
        if (!newItemInput.trim()) return

        const trimmedInput = newItemInput.trim()
        const newItem: ToolSupplyItem = { name: trimmedInput, out: false, in: false }
        
        if (itemType === "tool") {
            // Sort tools so Tool Kit is always first
            const toolsWithNewItem = [...toolsNeeded, newItem]
            const sortedTools = sortToolsWithToolKitFirst(toolsWithNewItem)
            setToolsNeeded(sortedTools)
        } else {
            setSuppliesNeeded([...suppliesNeeded, newItem])
        }
        
        // Close modal and reset input
        setIsAddItemModalOpen(false)
        setNewItemInput("")
    }

    const openAddItemModal = (type: "tool" | "supply") => {
        setItemType(type)
        setNewItemInput("")
        setIsAddItemModalOpen(true)
    }

    const handleCameraFABClick = () => { setIsCameraOpen(true) }

    const uploadPhotoImmediately = async (
        photoTempId: string,
        description?: string | null,
        overridePhoto?: { file: File; url: string; thumbnailUrl: string | null; takenAt?: string; tempId: string },
        takenAt?: string | null
    ): Promise<number | undefined> => {
        const photo = overridePhoto ?? capturedPhotos.find(p => p.tempId === photoTempId)
        const takenAtToUse = takenAt ?? (photo as CapturedPhoto)?.takenAt ?? overridePhoto?.takenAt
        const homeIdNum = workOrder?.home?.id
        if (!photo || !homeIdNum) return
        const isVideo = photo.file.type.startsWith('video/')
        if (photo.file.size === 0) throw new Error('File is empty and cannot be uploaded')
        if (uploadingPhotos.has(photoTempId)) return undefined
        setUploadingPhotos(prev => new Set(prev).add(photoTempId))
        try {
            const fileToUpload = new File([photo.file], photo.file.name, { type: photo.file.type, lastModified: photo.file.lastModified })
            const result = await uploadMultiplePhotosDirect(homeIdNum, [fileToUpload], workOrderId ?? undefined)
            if (!result.success) throw new Error(result.message || `Failed to upload ${isVideo ? 'video' : 'photo'}`)
            if (result.data && result.data.length > 0) {
                const uploadedPhoto = result.data[0]
                if (description || takenAtToUse) await updatePhoto(uploadedPhoto.id, { ...(description && { description }), ...(takenAtToUse && { takenAt: takenAtToUse }) })
                if (photo.thumbnailUrl) setLastSavedThumbnailUrl(photo.thumbnailUrl)
                setCapturedPhotos(prev => prev.filter(p => p.tempId !== photoTempId))
                URL.revokeObjectURL(photo.url)
                return uploadedPhoto.id
            }
            throw new Error(`${isVideo ? 'Video' : 'Photo'} uploaded but no data returned`)
        } catch (error) {
            alert(error instanceof Error ? error.message : `Failed to upload ${isVideo ? 'video' : 'photo'}`)
            return undefined
        } finally {
            setUploadingPhotos(prev => { const s = new Set(prev); s.delete(photoTempId); return s })
        }
    }

    const handleCameraCapture = (file: File) => {
        if (file.size === 0) { alert('Error: The captured file is empty. Please try again.'); return }
        const url = URL.createObjectURL(file)
        const tempId = `photo-${Date.now()}-${Math.random()}`
        const isVideo = file.type.startsWith('video/')
        if (!isVideo) setCurrentPhotoTempId(tempId)
        if (isVideo) {
            const video = document.createElement('video')
            video.src = url
            video.muted = true
            video.playsInline = true
            video.preload = 'metadata'
            let thumbnailGenerated = false
            let videoAdded = false
            const timeoutId = setTimeout(() => {
                if (!videoAdded) {
                    const newPhoto: CapturedPhoto = { file, url, thumbnailUrl: null, takenAt: new Date().toISOString(), tempId }
                    setCapturedPhotos(prev => [...prev, newPhoto])
                    setLastSavedThumbnailUrl(null)
                    videoAdded = true
                    uploadPhotoImmediately(tempId, null, newPhoto).catch(() => {})
                }
            }, 5000)
            const addVideoToArray = (thumbnailUrl: string | null) => {
                if (videoAdded) return
                videoAdded = true
                clearTimeout(timeoutId)
                const newPhoto: CapturedPhoto = { file, url, thumbnailUrl, takenAt: new Date().toISOString(), tempId }
                setCapturedPhotos(prev => [...prev, newPhoto])
                setLastSavedThumbnailUrl(null)
                uploadPhotoImmediately(tempId, null, newPhoto).catch(() => {})
            }
            video.onloadedmetadata = () => { try { video.currentTime = video.duration > 0 ? Math.min(1, Math.max(0.1, video.duration * 0.1)) : 0.1 } catch { video.currentTime = 0 } }
            video.onseeked = () => {
                if (thumbnailGenerated) return
                thumbnailGenerated = true
                try {
                    const canvas = document.createElement('canvas')
                    canvas.width = 48
                    canvas.height = 48
                    const ctx = canvas.getContext('2d')
                    if (ctx && video.videoWidth > 0 && video.videoHeight > 0) {
                        const scale = Math.max(48 / video.videoWidth, 48 / video.videoHeight)
                        const x = (48 - video.videoWidth * scale) / 2
                        const y = (48 - video.videoHeight * scale) / 2
                        ctx.drawImage(video, x, y, video.videoWidth * scale, video.videoHeight * scale)
                        canvas.toBlob((blob) => { if (blob) addVideoToArray(URL.createObjectURL(blob)); else addVideoToArray(null) }, 'image/jpeg', 0.8)
                    } else addVideoToArray(null)
                } catch { addVideoToArray(null) }
            }
            video.onerror = () => { clearTimeout(timeoutId); addVideoToArray(null) }
            video.oncanplay = () => {
                if (!thumbnailGenerated && !videoAdded && video.readyState >= 2) {
                    try {
                        const canvas = document.createElement('canvas')
                        canvas.width = 48
                        canvas.height = 48
                        const ctx = canvas.getContext('2d')
                        if (ctx && video.videoWidth > 0 && video.videoHeight > 0) {
                            const scale = Math.max(48 / video.videoWidth, 48 / video.videoHeight)
                            const x = (48 - video.videoWidth * scale) / 2
                            const y = (48 - video.videoHeight * scale) / 2
                            ctx.drawImage(video, x, y, video.videoWidth * scale, video.videoHeight * scale)
                            canvas.toBlob((blob) => { if (blob && !videoAdded) { thumbnailGenerated = true; addVideoToArray(URL.createObjectURL(blob)) } }, 'image/jpeg', 0.8)
                        }
                    } catch {}
                }
            }
            video.load()
        } else {
            const img = document.createElement('img')
            img.src = url
            img.onload = () => {
                const canvas = document.createElement('canvas')
                canvas.width = 48
                canvas.height = 48
                const ctx = canvas.getContext('2d')
                if (ctx) {
                    const scale = Math.max(48 / img.width, 48 / img.height)
                    const x = (48 - img.width * scale) / 2
                    const y = (48 - img.height * scale) / 2
                    ctx.drawImage(img, x, y, img.width * scale, img.height * scale)
                    canvas.toBlob((blob) => {
                        if (blob) {
                            const thumbnailUrl = URL.createObjectURL(blob)
                            setCapturedPhotos(prev => [...prev, { file, url, thumbnailUrl, takenAt: new Date().toISOString(), tempId }])
                            setLastSavedThumbnailUrl(null)
                            setShowPostCapturePopup(true)
                        }
                    }, 'image/jpeg', 0.8)
                }
            }
        }
    }

    const handlePostCaptureClose = (takenAt: string) => {
        setShowPostCapturePopup(false)
        if (currentPhotoTempId !== null && !isVoiceRecordingOpenRef.current && !isTextInputOpenRef.current && !isTextInputSavingRef.current) {
            const pid = currentPhotoTempId
            setCurrentPhotoTempId(null)
            uploadPhotoImmediately(pid, null, undefined, takenAt).catch(() => {})
        }
    }

    const cleanupAllPhotos = async () => {
        if (capturedPhotos.length > 0) {
            await Promise.allSettled(capturedPhotos.map(p => uploadPhotoImmediately(p.tempId, null, undefined, p.takenAt).catch(() => {})))
        }
        capturedPhotos.forEach(p => { URL.revokeObjectURL(p.url); if (p.thumbnailUrl) URL.revokeObjectURL(p.thumbnailUrl) })
        if (lastSavedThumbnailUrl) { URL.revokeObjectURL(lastSavedThumbnailUrl); setLastSavedThumbnailUrl(null) }
        setCapturedPhotos([])
        setCurrentPhotoTempId(null)
        setShowPostCapturePopup(false)
    }

    const handlePostCaptureVoiceRecord = (takenAt: string) => { currentTakenAtRef.current = takenAt; setShowPostCapturePopup(false); setIsVoiceRecordingOpen(true) }
    const handlePostCaptureTextInput = (takenAt: string) => { currentTakenAtRef.current = takenAt; setShowPostCapturePopup(false); setIsTextInputOpen(true) }

    const handleVoiceRecordingSave = async (audioBlob: Blob) => {
        setIsVoiceRecordingOpen(false)
        if (currentPhotoTempId === null) return
        const photoTempId = currentPhotoTempId
        setCurrentPhotoTempId(null)
        setPendingTranscriptions(prev => new Set(prev).add(photoTempId))
        let uploadedPhotoId: number | undefined
        try {
            uploadedPhotoId = await uploadPhotoImmediately(photoTempId, null, undefined, currentTakenAtRef.current)
            if (uploadedPhotoId) setCapturedPhotos(prev => prev.map(p => p.tempId === photoTempId ? { ...p, uploadedPhotoId } : p))
        } catch {
            setPendingTranscriptions(prev => { const s = new Set(prev); s.delete(photoTempId); return s })
            return
        }
        ;(async () => {
            try {
                const formData = new FormData()
                formData.append('audio', new File([audioBlob], 'recording.webm', { type: audioBlob.type || 'audio/webm' }))
                const res = await authFetch('/ai/transcribe', { method: 'POST', body: formData })
                const result = await res.json()
                if (result.success && result.data?.transcription && uploadedPhotoId)
                    await updatePhoto(uploadedPhotoId, { description: result.data.transcription })
            } catch {}
            finally { setPendingTranscriptions(prev => { const s = new Set(prev); s.delete(photoTempId); return s }) }
        })()
    }

    const handleTextInputSave = async (text: string) => {
        isTextInputSavingRef.current = true
        setIsTextInputOpen(false)
        if (currentPhotoTempId === null) { isTextInputSavingRef.current = false; return }
        const photoTempId = currentPhotoTempId
        setCurrentPhotoTempId(null)
        uploadPhotoImmediately(photoTempId, text, undefined, currentTakenAtRef.current).catch(() => {}).finally(() => { isTextInputSavingRef.current = false })
    }

    // Handle deleting a tool or supply
    const handleDeleteItem = (itemToDelete: ToolSupplyItem, type: "tool" | "supply") => {
        if (type === "tool") {
            // Remove item and sort so Tool Kit is always first
            const filtered = toolsNeeded.filter(item => item.name !== itemToDelete.name)
            const sortedTools = sortToolsWithToolKitFirst(filtered)
            setToolsNeeded(sortedTools)
        } else {
            setSuppliesNeeded(suppliesNeeded.filter(item => item.name !== itemToDelete.name))
        }
        // Status is stored in the object, so no separate tracking update needed
    }

    return (
        <DashboardContentWrapper className={cn(
            "space-y-6 print:space-y-2 print:pb-0",
            hasUnsavedChanges || showSuccessBar ? "pb-28 md:pb-20" : "pb-24 md:pb-6"
        )}>
            {/* Header Section */}
            <div className="space-y-4">
                {/* Top row - Work Scope and actions */}
                <div className="flex flex-col gap-2 lg:flex-row lg:items-center lg:justify-between print:flex-row print:items-center print:justify-between">
                    <div className="flex items-center gap-3">
                        <button onClick={() => router.back()} className="cursor-pointer print:hidden">
                            <ChevronLeft className="w-8 h-8 text-muted-foreground hover:text-foreground" />
                        </button>
                        <Heading className="text-3xl">
                            {formatWorkScope(workOrder.workOrderType)}
                        </Heading>
                    </div>
                    
                    <div className="hidden lg:flex items-center gap-2 text-muted-foreground lg:ml-auto print:ml-auto">
                        {workOrder.home?.id && (
                            <>
                                <Button
                                    onClick={handleCameraFABClick}
                                    className="flex items-center gap-2 bg-primary hover:bg-primary/90 text-white print:hidden"
                                    size="sm"
                                >
                                    <Camera className="w-4 h-4" />
                                    Take
                                </Button>
                                <Button
                                    onClick={() => setIsAddPhotoModalOpen(true)}
                                    className="flex items-center gap-2 print:hidden"
                                    size="sm"
                                >
                                    <PlusIcon className="w-4 h-4" />
                                    Upload
                                </Button>
                            </>
                        )}
                        {canManageWorkOrders && workOrderId && (
                            <>
                                <Button
                                    asChild
                                    size="sm"
                                    className="bg-primary hover:bg-primary/90 text-primary-foreground print:hidden"
                                    title="Edit Work Order"
                                >
                                    <Link
                                        href={buildRegionAwareHref(regionSlug, `/work-orders/edit/${workOrderId}`)}
                                    >
                                        <Pencil className="w-4 h-4" />
                                    </Link>
                                </Button>
                                <Button
                                    type="button"
                                    size="sm"
                                    onClick={handleDuplicate}
                                    disabled={isDuplicating}
                                    className="bg-primary hover:bg-primary/90 text-primary-foreground print:hidden"
                                    title="Duplicate Work Order"
                                    aria-label="Duplicate Work Order"
                                    showLoader={false}
                                >
                                    {isDuplicating ? (
                                        <Loader2 className="w-4 h-4 animate-spin" />
                                    ) : (
                                        <Copy className="w-4 h-4" />
                                    )}
                                </Button>
                            </>
                        )}
                        <PrintButton title="Print Work Order" onPreparePrint={prepareForPrint} />
                        {canManageWorkOrders && workOrderId && workOrder && (
                            <Button
                                type="button"
                                variant={workOrder.status === "Active" ? "default" : "outline"}
                                size="sm"
                                className="shrink-0 print:hidden"
                                onClick={() => handleStatusChange(workOrder.status === "Inactive")}
                                disabled={isUpdatingStatus}
                                aria-label={workOrder.status === "Active" ? "Close work order" : "Reopen work order"}
                                title={workOrder.status === "Active" ? "Close work order" : "Reopen work order"}
                            >
                                {workOrder.status === "Active" ? "Open" : "Closed"}
                            </Button>
                        )}
                    </div>
                </div>

                {/* Second row - Homeowner info and other details */}
                <div className="flex flex-col space-y-4 lg:flex-row lg:items-start lg:justify-between lg:space-y-0 print:flex-row print:items-stretch print:justify-between print:space-y-0 print:min-h-[120px]">
                    <div className="space-y-2 print:flex print:flex-col print:justify-between print:flex-1 print:pr-16">
                    {workOrder.home?.homeowner?.user?.fullname && (
                            <>
                                <div className="ml-11 print:ml-0 space-y-1">
                            {homeDetailsHref ? (
                                <Link href={homeDetailsHref} className="block text-muted-foreground text-lg hover:underline">
                                    {shouldMaskOwner ? maskHomeownerLastName(workOrder.home.homeowner.user.fullname) : workOrder.home.homeowner.user.fullname}
                                </Link>
                            ) : (
                                <p className="text-muted-foreground text-lg">
                                    {shouldMaskOwner ? maskHomeownerLastName(workOrder.home.homeowner.user.fullname) : workOrder.home.homeowner.user.fullname}
                                </p>
                            )}
                            {workOrder.home?.address && (
                                workOrder.home?.id ? (
                                    (() => {
                                        const inAppMapHref = buildRegionAwareHref(
                                            regionSlug,
                                            `${HOMEOWNERS_ROUTES.map}&homeId=${workOrder.home!.id}`,
                                        )
                                        const googleUrl = buildGoogleMapsPlaceUrl(
                                            workOrder.home!.address,
                                            workOrder.home!.latitude ?? null,
                                            workOrder.home!.longitude ?? null,
                                        )
                                        if (shouldMaskOwner) {
                                            return (
                                                <Link
                                                    href={inAppMapHref}
                                                    className="block text-muted-foreground text-sm hover:underline"
                                                >
                                                    {maskHomeownerAddress(workOrder.home!.address)}
                                                </Link>
                                            )
                                        }
                                        if (googleUrl !== "#") {
                                            return (
                                                <a
                                                    href={googleUrl}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="block text-muted-foreground text-sm hover:underline"
                                                >
                                                    {workOrder.home!.address}
                                                </a>
                                            )
                                        }
                                        return (
                                            <Link
                                                href={inAppMapHref}
                                                className="block text-muted-foreground text-sm hover:underline"
                                            >
                                                {workOrder.home!.address}
                                            </Link>
                                        )
                                    })()
                                ) : (
                                    <p className="text-muted-foreground text-sm">
                                        {shouldMaskOwner ? maskHomeownerAddress(workOrder.home.address) : workOrder.home.address}
                                    </p>
                                )
                            )}
                            {workOrder.home?.homeowner?.user?.phone && (
                                shouldMaskOwner ? (
                                    <p className="text-muted-foreground text-sm">
                                        {maskHomeownerPhone(workOrder.home.homeowner.user.phone)}
                                    </p>
                                ) : (
                                <a
                                    href={getPhoneTelLink(workOrder.home.homeowner.user.phone) ?? "#"}
                                    className="block text-muted-foreground text-sm hover:underline"
                                >
                                    {formatPhoneNumber(workOrder.home.homeowner.user.phone)}
                                </a>
                                )
                            )}
                            <div className="flex flex-wrap items-center gap-2 mt-2 print:hidden">
                                {homeDetailsBaseUrl && homeDetailsHref && (
                                    <Link
                                        href={homeDetailsHref}
                                        className={cn(buttonVariants({ variant: "default", size: "sm" }), "inline-flex items-center gap-2")}
                                        title="Home Details"
                                        aria-label="Home Details"
                                    >
                                        <ExternalLink className="w-4 h-4" />
                                        <span className="max-lg:hidden">Home Details</span>
                                    </Link>
                                )}
                                {workOrder.home?.id && (
                                    <>
                                        <Button
                                            onClick={handleCameraFABClick}
                                            size="sm"
                                            className="lg:hidden flex items-center gap-2 bg-primary hover:bg-primary/90 text-white"
                                            title="Take"
                                            aria-label="Take"
                                        >
                                            <Camera className="w-4 h-4" />
                                            <span className="max-sm:hidden">Take</span>
                                        </Button>
                                        <Button
                                            onClick={() => setIsAddPhotoModalOpen(true)}
                                            size="sm"
                                            className="lg:hidden flex items-center gap-2"
                                            title="Upload"
                                            aria-label="Upload"
                                        >
                                            <PlusIcon className="w-4 h-4" />
                                            <span className="max-sm:hidden">Upload</span>
                                        </Button>
                                    </>
                                )}
                                {canManageWorkOrders && workOrderId && (
                                    <>
                                        <Button
                                            asChild
                                            size="sm"
                                            className="lg:hidden bg-primary hover:bg-primary/90 text-primary-foreground"
                                            title="Edit Work Order"
                                            aria-label="Edit Work Order"
                                        >
                                            <Link href={buildRegionAwareHref(regionSlug, `/work-orders/edit/${workOrderId}`)}>
                                                <Pencil className="w-4 h-4" />
                                            </Link>
                                        </Button>
                                        <Button
                                            type="button"
                                            size="sm"
                                            onClick={handleDuplicate}
                                            disabled={isDuplicating}
                                            className="lg:hidden bg-primary hover:bg-primary/90 text-primary-foreground"
                                            title="Duplicate Work Order"
                                            aria-label="Duplicate Work Order"
                                            showLoader={false}
                                        >
                                            {isDuplicating ? (
                                                <Loader2 className="w-4 h-4 animate-spin" />
                                            ) : (
                                                <Copy className="w-4 h-4" />
                                            )}
                                        </Button>
                                    </>
                                )}
                                <div className="lg:hidden flex items-center gap-2">
                                    <PrintButton title="Print Work Order" onPreparePrint={prepareForPrint} />
                                    {canManageWorkOrders && workOrderId && workOrder && (
                                        <Button
                                            type="button"
                                            variant={workOrder.status === "Active" ? "default" : "outline"}
                                            size="sm"
                                            className="shrink-0"
                                            onClick={() => handleStatusChange(workOrder.status === "Inactive")}
                                            disabled={isUpdatingStatus}
                                            aria-label={workOrder.status === "Active" ? "Close work order" : "Reopen work order"}
                                            title={workOrder.status === "Active" ? "Close work order" : "Reopen work order"}
                                        >
                                            {workOrder.status === "Active" ? "Open" : "Closed"}
                                        </Button>
                                    )}
                                </div>
                            </div>
                        </div>
                                
                                {/* Total Hours Worked - Print Only */}
                                <div className="hidden print:flex print:items-end print:gap-3 mt-6">
                                    <p className="text-sm font-medium text-foreground whitespace-nowrap pb-1">
                                        Total Hours Worked:
                                    </p>
                                    <div className="border-b-2 border-gray-400 flex-1 mb-1"></div>
                                </div>
                            </>
                    )}
                </div>

                    <div className="text-left space-y-2 lg:text-right lg:space-y-3 print:text-right">
                    {/* Total Work Hours Display */}
                        <div className="bg-blue-50 border border-blue-200 rounded-lg px-4 py-2 flex justify-between items-center gap-2 w-full lg:w-fit text-center lg:text-left print:hidden">
                        <div className="text-blue-900 font-medium">
                            Total Work Hours:
                        </div>
                        <div className="text-blue-900 font-semibold">
                            {workOrderHours.reduce((sum, entry) => sum + (entry.hoursWorked || 0), 0)}h
                            </div>
                        </div>

                        {/* QR Code - Shows on print */}
                        <div className="hidden print:flex print:flex-col print:items-center gap-1">
                            <QRCode 
                                value={`https://www.reliefhub.app/work-orders/${workOrderId}`}
                                size={100}
                                level="M"
                            />
                            <p className="text-xs text-center text-muted-foreground mt-1">
                                Scan to view this work order online
                            </p>
                        </div>
                    </div>
                </div>
            </div>

            {/* Main Content - Two Column Layout with Nested Grid */}
            <div className="grid grid-cols-1 lg:grid-cols-2 print:grid-cols-2 gap-6 print:break-inside-auto">
                {/* Left Column - Construction Manager, Team Name, Team Leader (with nested 2-column grid) */}
                <div className="grid grid-cols-[auto_1fr] gap-x-8 gap-y-4 items-center p-3 pb-0">
                    {/* Construction Manager Label */}
                    <div className="flex items-center gap-2">
                        <User className="w-5 h-5 text-muted-foreground flex-shrink-0" />
                        <span className="text-muted-foreground font-medium text-sm whitespace-nowrap">
                            Construction Manager
                        </span>
                    </div>
                    {/* Construction Manager Value */}
                    <div>
                        <p className="text-foreground font-semibold text-sm truncate">
                            {workOrder?.owner?.fullname || "N/A"}
                        </p>
                        <p className="text-muted-foreground text-xs">
                            {formatPhoneNumber(workOrder?.owner?.phone)}
                        </p>
                    </div>

                    {/* Team Name Label */}
                    <div className="flex items-center gap-2">
                        <Users className="w-5 h-5 text-muted-foreground flex-shrink-0" />
                        <span className="text-muted-foreground font-medium text-sm whitespace-nowrap">
                            Team Name
                        </span>
                    </div>
                    {/* Team Name Value */}
                    <div>
                        <p className="text-foreground font-semibold text-sm truncate">
                            {workOrder.teamName || "N/A"}
                        </p>
                    </div>

                    {/* Team Leader Label */}
                    <div className="flex items-center gap-2">
                        <Users className="w-5 h-5 text-muted-foreground flex-shrink-0" />
                        <span className="text-muted-foreground font-medium text-sm whitespace-nowrap">
                            Team Leader
                        </span>
                    </div>
                    {/* Team Leader Value */}
                    <div>
                        <p className="text-foreground font-semibold text-sm truncate">
                            {workOrder.teamLead?.fullname || "N/A"}
                        </p>
                        <p className="text-muted-foreground text-xs">
                            {formatPhoneNumber(workOrder?.teamLead?.phone)}
                        </p>
                    </div>
                </div>

                {/* Right Column - Volunteers */}
                <div className="p-3 flex flex-col">
                    {/* Volunteer Label */}
                    <div className="flex items-center justify-between mb-4">
                        <div className="flex items-center gap-2">
                        <UserCheck className="w-5 h-5 text-muted-foreground flex-shrink-0" />
                        <span 
                            className={cn(
                                "font-medium text-sm",
                                isVolunteersModified() ? "text-purple-600" : "text-muted-foreground"
                            )}
                            style={isVolunteersModified() ? { color: '#9c27b0' } : undefined}
                        >
                            Volunteers
                        </span>
                        </div>
                    </div>

                    {!isVolunteer ? (
                        <>
                            {/* Volunteers Multiselect with Filters */}
                            <div className="mb-4 print:hidden">
                                <MultiSelectWithFilters
                                    options={volunteerOptions}
                                    selectedValues={selectedVolunteerIds}
                                    onSelectionChange={setSelectedVolunteerIds}
                                    placeholder="Select volunteers..."
                                    searchPlaceholder="Search volunteers..."
                                    customHeight="h-[100px]"
                                    organizationOptions={organizationOptions}
                                />
                            </div>
                            
                            {/* Display selected volunteers for print */}
                            <div className="hidden print:grid grid-cols-2 gap-1 flex-1">
                                {selectedVolunteerIds.length > 0 ? (
                                    selectedVolunteerIds.map((id) => {
                                        const volunteer = allVolunteers.find(v => v.id.toString() === id)
                                        return volunteer ? (
                                            <div
                                                key={volunteer.id}
                                                className="flex items-center gap-3 min-w-0 rounded-lg py-0.5 px-2"
                                            >
                                                <div className="min-w-0 flex-1">
                                                    <p className="text-foreground font-semibold text-sm truncate">
                                                        {volunteer.user.fullname}
                                                    </p>
                                                </div>
                                            </div>
                                        ) : null
                                    })
                                ) : (
                                    <div className="col-span-full text-center text-muted-foreground py-4">
                                        No volunteers assigned to this work order
                                    </div>
                                )}
                            </div>
                        </>
                    ) : (
                        /* Display for volunteers (read-only) */
                        <div className="grid grid-cols-2 gap-1 flex-1">
                            {volunteers.length > 0 ? (
                                volunteers.map((volunteer) => {
                                    return (
                                        <div
                                            key={volunteer.id}
                                            className="flex items-center gap-3 min-w-0 rounded-lg py-0.5 px-2"
                                        >
                                            <div className="min-w-0 flex-1">
                                                <p className="text-foreground font-semibold text-sm truncate">
                                                    {volunteer.user.fullname}
                                                </p>
                                            </div>
                                        </div>
                                    )
                                })
                            ) : (
                                <div className="col-span-full text-center text-muted-foreground py-4">
                                    No volunteers assigned to this work order
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </div>

            {/* Work Order Hours and Progress Tracking - Combined Section */}
            {!isVolunteer && (
                <div className="border-t border-gray-200 pt-3 print:hidden">
                {/* <div className="grid grid-cols-1 lg:grid-cols-2 gap-8"> */}
                    {/* Left Column - Work Order Hours */}
                    <div>
                {/* Field Headers */}
                <div className="flex gap-1 items-center mb-3">
                    <div className="w-52 flex-shrink-0">
                        <Label 
                            className="text-sm font-medium" 
                            style={{ 
                                color: JSON.stringify(workOrderHours) !== JSON.stringify(originalWorkOrderHours) ? "#9c27b0" : "#003764" 
                            }}
                        >
                            Work Day
                        </Label>
                    </div>
                    <div className="w-24 flex-shrink-0">
                        <Label 
                            className="text-sm font-medium" 
                            style={{ 
                                color: JSON.stringify(workOrderHours) !== JSON.stringify(originalWorkOrderHours) ? "#9c27b0" : "#003764" 
                            }}
                        >
                            Total Hours
                        </Label>
                    </div>
                    <div className="w-10 flex-shrink-0"></div>
                </div>
                
                <div className="space-y-3">
                    {workOrderHours.map((entry, index) => (
                        <div key={index} className="flex gap-1 items-center w-fit">
                            {/* Date Picker */}
                            <div className="w-52 flex-shrink-0">
                                <WorkOrderHoursDatepicker
                                    value={entry.date}
                                    onSelect={(date) => {
                                        const newHours = [...workOrderHours]
                                        newHours[index] = {
                                            ...newHours[index],
                                            date: date ? formatDateForDB(date) : null
                                        }
                                        setWorkOrderHours(newHours)
                                    }}
                                    disabled={isVolunteer}
                                    isError={isDateRequired(index)}
                                    isModified={isFieldModified(index, 'date') && !isDateRequired(index)}
                                    placeholder={isDateRequired(index) ? "Date required" : "Pick date"}
                                    storageFormat="string"
                                />
                            </div>

                            {/* Hours Input */}
                            <div className="w-24 flex-shrink-0">
                                <Input
                                    type="number"
                                    min="0"
                                    step="0.5"
                                    placeholder="Hours"
                                    value={entry.hoursWorked || ""}
                                    onChange={(e) => {
                                        const newHours = [...workOrderHours]
                                        newHours[index] = {
                                            ...newHours[index],
                                            hoursWorked: parseFloat((e.target as HTMLInputElement).value) || 0
                                        }
                                        setWorkOrderHours(newHours)
                                    }}
                                    className={cn(
                                        "w-full [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none",
                                        isFieldModified(index, 'hoursWorked') && "border-2 !border-[#9c27b0]"
                                    )}
                                    disabled={isVolunteer}
                                />
                            </div>

                            {/* Remove button (show when there are 2+ rows) */}
                            <div className="w-10 flex-shrink-0">
                                {workOrderHours.length > 1 && (
                                    <Button
                                        type="button"
                                        variant="ghost"
                                        size="icon"
                                        className="h-10 w-10"
                                        onClick={() => {
                                            setWorkOrderHours(workOrderHours.filter((_, i) => i !== index))
                                        }}
                                        disabled={isVolunteer}
                                    >
                                        <X className="h-4 w-4" />
                                    </Button>
                                )}
                            </div>
                        </div>
                    ))}

                    {/* Add Work Days Range */}
                    <div className="w-full max-w-md">
                        <DateRangePickerControl
                            label="Add Work Days"
                            value={addWorkDaysRange}
                            onChange={(nextRange) => {
                                setAddWorkDaysRange(nextRange)
                                if (nextRange.from && nextRange.to) {
                                    addWorkDaysFromRange(nextRange)
                                }
                            }}
                            disabled={isVolunteer}
                        />
                    </div>
                </div>
            </div>

                    {/* Right Column - Progress Tracking */}
                    {/* <div>
                        <ProgressTrackingForm
                            workOrderId={workOrderId || 0}
                            initialProgress={workOrder?.progress?.toString() || "0"}
                            progress={progress}
                            onProgressChange={setProgress}
                            isModified={isProgressModified()}
                        />
                    </div> */}
                {/* </div> */}
            </div>
            )}

            {/* Description Section */}
            <div className="border-t border-gray-200 pt-2 pb-2">
                <h3
                    className="text-lg font-semibold mb-2 print:mb-2"
                    style={{ color: "#003764" }}
                >
                    Description
                </h3>
                <div className="text-gray-600 leading-relaxed">
                    {workOrder.projectDescription ? (
                        <p>{workOrder.projectDescription}</p>
                    ) : (
                        <p className="text-gray-400 italic">
                            No description provided for this work order.
                        </p>
                    )}
                </div>
            </div>

            {/* Check List Section */}
            <div ref={checklistSectionRef} className="border-t border-gray-200 pt-2 pb-2">
                <div className="grid grid-cols-1 lg:grid-cols-2 print:grid-cols-2 gap-8">
                    <div ref={toolsColumnRef} className="lg:border-r print:border-r lg:border-gray-200 print:border-gray-200 lg:pr-8 print:pr-8">
                        {/* Tools Check List */}
                        <div>
                            <h3
                                className={cn(
                                    "text-lg font-semibold mb-2 print:mb-2",
                                    isToolsModified() ? "text-purple-600" : ""
                                )}
                                style={isToolsModified() ? { color: '#9c27b0' } : { color: "#003764" }}
                            >
                                Tools Needed
                            </h3>
                            
                            {/* Column Headers */}
                            <div className={cn(
                                "gap-2 mb-2 text-xs font-semibold text-gray-600",
                                isVolunteer 
                                    ? "grid grid-cols-[1fr_3rem_3rem] print:grid-cols-[minmax(0,1fr)_3rem_3rem]"
                                    : "grid grid-cols-[1fr_3rem_3rem_auto] print:grid-cols-[minmax(0,1fr)_3rem_3rem]"
                            )}>
                                <div></div>
                                <div className="text-center">Out</div>
                                <div className="text-center">In</div>
                                {!isVolunteer && <div className="w-8 print:hidden"></div>}
                            </div>
                            
                            <div ref={toolsItemsRef} className="flex flex-col gap-2">
                                <div
                                    ref={rowHeightMeasureRef}
                                    className="fixed -left-[9999px] top-0 w-[480px] opacity-0 pointer-events-none"
                                    aria-hidden
                                >
                                    <ToolSupplyBlankLineRow isVolunteer={isVolunteer} />
                                </div>
                                {sortedToolsNeeded && sortedToolsNeeded.length > 0 ? (
                                    sortedToolsNeeded.map(
                                        (tool: ToolSupplyItem, index: number) => (
                                            <div key={index} className="flex flex-col gap-2">
                                                <div
                                                    className={cn(
                                                        "gap-2 items-center",
                                                        isVolunteer 
                                                            ? "grid grid-cols-[1fr_3rem_3rem] print:grid-cols-[minmax(0,1fr)_3rem_3rem]"
                                                            : "grid grid-cols-[1fr_3rem_3rem_auto] print:grid-cols-[minmax(0,1fr)_3rem_3rem]"
                                                    )}
                                                >
                                                    <div className="text-sm text-gray-700 min-w-0 truncate flex items-center gap-2" title={tool.name}>
                                                    {tool.name}
                                                    </div>
                                                    <div className="flex justify-center">
                                                        <input 
                                                            type="checkbox" 
                                                            className="w-5 h-5 cursor-pointer" 
                                                            checked={tool.out}
                                                            onChange={(e) => {
                                                                // Find the corresponding item in toolsNeeded by matching the tool object
                                                                // Since sortedToolsNeeded is derived from toolsNeeded, we need to find the exact match
                                                                // For blank lines, we match by position in the sorted array
                                                                const sorted = sortToolsWithToolKitFirst([...toolsNeeded])
                                                                const toolAtSortedIndex = sorted[index]
                                                                
                                                                // Find this exact item in the original toolsNeeded array
                                                                // Match by all properties to uniquely identify it (important for blank lines)
                                                                const actualIndex = toolsNeeded.findIndex((t, i) => {
                                                                    // First try to match by reference (if it's the same object)
                                                                    if (t === toolAtSortedIndex) return true
                                                                    // Then try to match by all properties
                                                                    if (t.name === toolAtSortedIndex.name && 
                                                                        t.out === toolAtSortedIndex.out && 
                                                                        t.in === toolAtSortedIndex.in) {
                                                                        // For items with same name/state, count how many appear before this position in sorted array
                                                                        const sortedBefore = sorted.slice(0, index).filter(st => 
                                                                            st.name === toolAtSortedIndex.name && 
                                                                            st.out === toolAtSortedIndex.out && 
                                                                            st.in === toolAtSortedIndex.in
                                                                        ).length
                                                                        // Count how many appear before this index in original array
                                                                        const originalBefore = toolsNeeded.slice(0, i).filter(ot => 
                                                                            ot.name === toolAtSortedIndex.name && 
                                                                            ot.out === toolAtSortedIndex.out && 
                                                                            ot.in === toolAtSortedIndex.in
                                                                        ).length
                                                                        return sortedBefore === originalBefore
                                                                    }
                                                                    return false
                                                                })
                                                                
                                                                if (actualIndex >= 0) {
                                                                    const updatedTools = toolsNeeded.map((t, i) => 
                                                                        i === actualIndex 
                                                                            ? { ...t, out: e.target.checked }
                                                                            : t
                                                                    )
                                                                    setToolsNeeded(updatedTools)
                                                                }
                                                            }}
                                                        />
                                                    </div>
                                                    <div className="flex justify-center">
                                                        <input 
                                                            type="checkbox" 
                                                            className="w-5 h-5 cursor-pointer" 
                                                            checked={tool.in}
                                                            onChange={(e) => {
                                                                // Find the corresponding item in toolsNeeded by matching the tool object
                                                                // Since sortedToolsNeeded is derived from toolsNeeded, we need to find the exact match
                                                                // For blank lines, we match by position in the sorted array
                                                                const sorted = sortToolsWithToolKitFirst([...toolsNeeded])
                                                                const toolAtSortedIndex = sorted[index]
                                                                
                                                                // Find this exact item in the original toolsNeeded array
                                                                // Match by all properties to uniquely identify it (important for blank lines)
                                                                const actualIndex = toolsNeeded.findIndex((t, i) => {
                                                                    // First try to match by reference (if it's the same object)
                                                                    if (t === toolAtSortedIndex) return true
                                                                    // Then try to match by all properties
                                                                    if (t.name === toolAtSortedIndex.name && 
                                                                        t.out === toolAtSortedIndex.out && 
                                                                        t.in === toolAtSortedIndex.in) {
                                                                        // For items with same name/state, count how many appear before this position in sorted array
                                                                        const sortedBefore = sorted.slice(0, index).filter(st => 
                                                                            st.name === toolAtSortedIndex.name && 
                                                                            st.out === toolAtSortedIndex.out && 
                                                                            st.in === toolAtSortedIndex.in
                                                                        ).length
                                                                        // Count how many appear before this index in original array
                                                                        const originalBefore = toolsNeeded.slice(0, i).filter(ot => 
                                                                            ot.name === toolAtSortedIndex.name && 
                                                                            ot.out === toolAtSortedIndex.out && 
                                                                            ot.in === toolAtSortedIndex.in
                                                                        ).length
                                                                        return sortedBefore === originalBefore
                                                                    }
                                                                    return false
                                                                })
                                                                
                                                                if (actualIndex >= 0) {
                                                                    const updatedTools = toolsNeeded.map((t, i) => 
                                                                        i === actualIndex 
                                                                            ? { ...t, in: e.target.checked }
                                                                            : t
                                                                    )
                                                                    setToolsNeeded(updatedTools)
                                                                }
                                                            }}
                                                        />
                                                    </div>
                                                    {!isVolunteer && (
                                                        <Button
                                                            type="button"
                                                            variant="ghost"
                                                            size="icon"
                                                            className="h-8 w-8 print:hidden"
                                                            onClick={() => handleDeleteItem(tool, "tool")}
                                                        >
                                                            <X className="h-4 w-4 text-gray-500 hover:text-red-600" />
                                                        </Button>
                                                    )}
                                                </div>
                                                {isToolKit(tool.name) && (
                                                    <div className="print:hidden">
                                                        <Accordion type="single" collapsible className="w-full">
                                                            <AccordionItem value="tool-kit-contents" className="border rounded-md px-3">
                                                                <AccordionTrigger className="text-xs py-2">
                                                                    View Tool Kit Contents
                                                                </AccordionTrigger>
                                                                <AccordionContent className="pt-2 pb-4">
                                                                    <div className="space-y-4 text-sm">
                                                                        {TOOL_KIT_CONTENTS.map((section, sectionIndex) => (
                                                                            <div key={sectionIndex} className="space-y-2">
                                                                                <h4 className="font-semibold text-xs">{section.title}</h4>
                                                                                <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs">
                                                                                    {section.items.map((item, itemIndex) => (
                                                                                        <div key={itemIndex} className="flex justify-between">
                                                                                            <span className="text-gray-700">{item.name}</span>
                                                                                            <span className="text-gray-500 font-medium">{item.quantity}</span>
                                                                                        </div>
                                                                                    ))}
                                                                                </div>
                                                                            </div>
                                                                        ))}
                                                                    </div>
                                                                </AccordionContent>
                                                            </AccordionItem>
                                                        </Accordion>
                                                    </div>
                                                )}
                                            </div>
                                        )
                                    )
                                ) : (
                                    <div className="text-gray-400 italic text-sm">
                                        No tools specified
                                    </div>
                                )}
                                {Array.from({ length: printBlankLines.tools }).map((_, i) => (
                                    <ToolSupplyBlankLineRow
                                        key={`print-blank-tool-${i}`}
                                        isVolunteer={isVolunteer}
                                        className="!hidden print:!grid"
                                    />
                                ))}
                            </div>
                            
                            {/* Add Tool Button */}
                            {!isVolunteer && (
                                <div className="mt-4 print:hidden">
                                    <Button
                                        type="button"
                                        variant="outline"
                                        size="sm"
                                        onClick={() => openAddItemModal("tool")}
                                    >
                                        <Plus className="w-4 h-4 mr-2" />
                                        Add Tool
                                    </Button>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Supplies Check List */}
                    <div ref={suppliesColumnRef} className="lg:pl-8 print:pl-8">
                        <div>
                            <h3
                                className={cn(
                                    "text-lg font-semibold mb-2 print:mb-2",
                                    isSuppliesModified() ? "text-purple-600" : ""
                                )}
                                style={isSuppliesModified() ? { color: '#9c27b0' } : { color: "#003764" }}
                            >
                                Supplies Needed
                            </h3>
                            
                            {/* Column Headers */}
                            <div className={cn(
                                "gap-2 mb-2 text-xs font-semibold text-gray-600",
                                isVolunteer 
                                    ? "grid grid-cols-[1fr_3rem_3rem] print:grid-cols-[minmax(0,1fr)_3rem_3rem]"
                                    : "grid grid-cols-[1fr_3rem_3rem_auto] print:grid-cols-[minmax(0,1fr)_3rem_3rem]"
                            )}>
                                <div></div>
                                <div className="text-center">Out</div>
                                <div className="text-center">In</div>
                                {!isVolunteer && <div className="w-8 print:hidden"></div>}
                            </div>
                            
                            <div ref={suppliesItemsRef} className="flex flex-col gap-2">
                                {displaySuppliesNeeded && displaySuppliesNeeded.length > 0 ? (
                                    displaySuppliesNeeded.map(
                                        (supply: ToolSupplyItem, index: number) => (
                                            <div
                                                key={index}
                                                className={cn(
                                                    "gap-2 items-center",
                                                    isVolunteer 
                                                        ? "grid grid-cols-[1fr_3rem_3rem] print:grid-cols-[minmax(0,1fr)_3rem_3rem]"
                                                        : "grid grid-cols-[1fr_3rem_3rem_auto] print:grid-cols-[minmax(0,1fr)_3rem_3rem]"
                                                )}
                                            >
                                                <div className="text-sm text-gray-700 min-w-0 truncate" title={supply.name}>
                                                    {supply.name}
                                                </div>
                                                <div className="flex justify-center">
                                                    <input 
                                                        type="checkbox" 
                                                        className="w-5 h-5 cursor-pointer" 
                                                        checked={supply.out}
                                                        onChange={(e) => {
                                                            const supply = displaySuppliesNeeded[index]
                                                            const actualIndex = suppliesNeeded.findIndex(
                                                                (s) =>
                                                                    s.name === supply.name &&
                                                                    s.out === supply.out &&
                                                                    s.in === supply.in
                                                            )
                                                            if (actualIndex < 0) return
                                                            const updatedSupplies = suppliesNeeded.map((s, i) =>
                                                                i === actualIndex
                                                                    ? { ...s, out: e.target.checked }
                                                                    : s
                                                            )
                                                            setSuppliesNeeded(updatedSupplies)
                                                        }}
                                                    />
                                                </div>
                                                <div className="flex justify-center">
                                                    <input 
                                                        type="checkbox" 
                                                        className="w-5 h-5 cursor-pointer" 
                                                        checked={supply.in}
                                                        onChange={(e) => {
                                                            const supply = displaySuppliesNeeded[index]
                                                            const actualIndex = suppliesNeeded.findIndex(
                                                                (s) =>
                                                                    s.name === supply.name &&
                                                                    s.out === supply.out &&
                                                                    s.in === supply.in
                                                            )
                                                            if (actualIndex < 0) return
                                                            const updatedSupplies = suppliesNeeded.map((s, i) =>
                                                                i === actualIndex
                                                                    ? { ...s, in: e.target.checked }
                                                                    : s
                                                            )
                                                            setSuppliesNeeded(updatedSupplies)
                                                        }}
                                                    />
                                                </div>
                                                {!isVolunteer && (
                                                    <Button
                                                        type="button"
                                                        variant="ghost"
                                                        size="icon"
                                                        className="h-8 w-8 print:hidden"
                                                        onClick={() => handleDeleteItem(supply, "supply")}
                                                    >
                                                        <X className="h-4 w-4 text-gray-500 hover:text-red-600" />
                                                    </Button>
                                                )}
                                            </div>
                                        )
                                    )
                                ) : (
                                    <div className="text-gray-400 italic text-sm">
                                        No supplies specified
                                    </div>
                                )}
                                {Array.from({ length: printBlankLines.supplies }).map((_, i) => (
                                    <ToolSupplyBlankLineRow
                                        key={`print-blank-supply-${i}`}
                                        isVolunteer={isVolunteer}
                                        className="!hidden print:!grid"
                                    />
                                ))}
                            </div>

                            {/* Add Supply Button */}
                            {!isVolunteer && (
                                <div className="mt-4 print:hidden">
                                    <Button
                                        type="button"
                                        variant="outline"
                                        size="sm"
                                        onClick={() => openAddItemModal("supply")}
                                    >
                                        <Plus className="w-4 h-4 mr-2" />
                                        Add Supply
                                    </Button>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            {/* Training Links and Reminders Section 
            <div className="pt-3 pb-3">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                    <div className="lg:border-r lg:border-gray-200 lg:pr-8 print:hidden">
                        <div>
                            <h3
                                className="text-lg font-semibold mb-4"
                                style={{ color: "#003764" }}
                            >
                                Training Links
                            </h3>
                            <div className="flex flex-col gap-0">
                                {workOrder.trainingLinks &&
                                Array.isArray(workOrder.trainingLinks) &&
                                workOrder.trainingLinks.length > 0 ? (
                                    workOrder.trainingLinks.map(
                                        (link: string, index: number) => (
                                            <div
                                                key={index}
                                                className="flex items-center justify-between space-x-2 p-1"
                                            >
                                                <div className="flex items-center space-x-2 flex-1 min-w-0">
                                                    <ExternalLink
                                                        className="w-4 h-4 flex-shrink-0"
                                                        style={{
                                                            color: "#81a2b6",
                                                        }}
                                                    />
                                                    <a
                                                        href={
                                                            link.startsWith(
                                                                "http://"
                                                            ) ||
                                                            link.startsWith(
                                                                "https://"
                                                            )
                                                                ? link
                                                                : `https://${link}`
                                                        }
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        className="text-sm text-gray-700 hover:underline break-all"
                                                    >
                                                        {link}
                                                    </a>
                                                </div>
                                                <Button
                                                    type="button"
                                                    variant="ghost"
                                                    size="sm"
                                                    className="h-6 w-6 p-0 flex-shrink-0 hover:bg-gray-100"
                                                    style={{
                                                        color: "#81a2b6",
                                                    }}
                                                    onClick={() =>
                                                        handleRemoveTrainingLink(
                                                            link
                                                        )
                                                    }
                                                    disabled={
                                                        deletingLink === link
                                                    }
                                                    showLoader={false}
                                                >
                                                    {deletingLink === link ? (
                                                        <Loader2 className="h-3 w-3 animate-spin" />
                                                    ) : (
                                                        <X className="h-3 w-3" />
                                                    )}
                                                </Button>
                                            </div>
                                        )
                                    )
                                ) : (
                                    <div className="text-gray-400 italic text-sm">
                                        No training links available
                                    </div>
                                )}
                            </div>

                            <div className="flex justify-start mt-4">
                                <Button
                                    type="button"
                                    className={cn(
                                        "h-8 px-3 text-sm text-foreground",
                                        isVolunteer &&
                                            "opacity-50 pointer-events-none"
                                    )}
                                    variant="secondary"
                                    size="sm"
                                    onClick={() => setIsAddLinkModalOpen(true)}
                                    disabled={isVolunteer}
                                    showLoader={false}
                                >
                                    <Plus className="w-4 h-4 mr-1" />
                                    Add Link
                                </Button>
                            </div>
                        </div>
                    </div>

                    <div className="lg:pl-8 print:hidden">
                        <div>
                            <h3
                                className="text-lg font-semibold mb-4"
                                style={{ color: "#003764" }}
                            >
                                Reminder
                            </h3>
                            <div className="text-gray-600 leading-relaxed">
                                {workOrder.reminders ? (
                                    <p>{workOrder.reminders}</p>
                                ) : (
                                    <p className="text-gray-400 italic">
                                        No reminders available
                                    </p>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <Dialog
                open={isAddLinkModalOpen}
                onOpenChange={setIsAddLinkModalOpen}
            >
                <DialogContent className="sm:max-w-md">
                    <DialogHeader>
                        <DialogTitle>Add Training Link</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4">
                        <div className="space-y-2">
                            <Label htmlFor="trainingLink">Link URL</Label>
                            <Input
                                id="trainingLink"
                                placeholder="Enter training link URL"
                                value={newLinkInput}
                                onChange={(e) =>
                                    setNewLinkInput(
                                        (e.target as HTMLInputElement).value
                                    )
                                }
                            />
                        </div>

                        <div className="flex gap-2 pt-4">
                            <Button
                                variant="outline"
                                onClick={() => {
                                    setIsAddLinkModalOpen(false)
                                    setNewLinkInput("")
                                }}
                                className="flex-1"
                            >
                                Cancel
                            </Button>
                            <Button
                                onClick={handleAddTrainingLink}
                                className="flex-1"
                            >
                                Add Link
                            </Button>
                        </div>
                    </div>
                </DialogContent>
            </Dialog>
            */}

            {/* Add Tool/Supply Modal */}
            <Dialog
                open={isAddItemModalOpen}
                onOpenChange={setIsAddItemModalOpen}
            >
                <DialogContent className="sm:max-w-md">
                    <DialogHeader>
                        <DialogTitle>Add {itemType === "tool" ? "Tool" : "Supply"}</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4">
                        <div className="space-y-2">
                            <Label htmlFor="newItem">{itemType === "tool" ? "Tool" : "Supply"} Name</Label>
                            <Input
                                id="newItem"
                                placeholder={`Enter ${itemType === "tool" ? "tool" : "supply"} name`}
                                value={newItemInput}
                                onChange={(e) =>
                                    setNewItemInput(
                                        (e.target as HTMLInputElement).value
                                    )
                                }
                                onKeyDown={(e) => {
                                    if (e.key === "Enter") {
                                        e.preventDefault()
                                        handleAddItem()
                                    }
                                }}
                            />
                        </div>

                        <div className="flex gap-2 pt-4">
                            <Button
                                variant="outline"
                                onClick={() => {
                                    setIsAddItemModalOpen(false)
                                    setNewItemInput("")
                                }}
                                className="flex-1"
                            >
                                Cancel
                            </Button>
                            <Button
                                onClick={handleAddItem}
                                className="flex-1"
                            >
                                Add
                            </Button>
                        </div>
                    </div>
                </DialogContent>
            </Dialog>

            {/* Add Photo Modal */}
            {workOrder.home?.id && workOrderId && (
                <AddPhotoModal
                    isOpen={isAddPhotoModalOpen}
                    onClose={() => setIsAddPhotoModalOpen(false)}
                    homeId={workOrder.home.id.toString()}
                    workOrderId={workOrderId.toString()}
                />
            )}

            {/* Camera Capture Modal (Take Photo - match HD Photos) */}
            {workOrder.home?.id && (
                <CameraCaptureModal
                    isOpen={isCameraOpen}
                    onClose={async () => {
                        await cleanupAllPhotos()
                        setIsCameraOpen(false)
                    }}
                    onCapture={handleCameraCapture}
                    lastCapturedThumbnail={capturedPhotos.length > 0 ? capturedPhotos[capturedPhotos.length - 1].thumbnailUrl : lastSavedThumbnailUrl}
                    hideDone={showPostCapturePopup || isVoiceRecordingOpen || isTextInputOpen}
                />
            )}

            {/* Post-Capture Popup */}
            {showPostCapturePopup && currentPhotoTempId !== null && (() => {
                const currentPhoto = capturedPhotos.find(p => p.tempId === currentPhotoTempId)
                return currentPhoto ? (
                    <PostCapturePopup
                        imageUrl={currentPhoto.url}
                        initialTakenAt={currentPhoto.takenAt}
                        onTakenAtChange={(takenAt) => setCapturedPhotos(prev => prev.map(p => p.tempId === currentPhotoTempId ? { ...p, takenAt } : p))}
                        onVoiceRecord={handlePostCaptureVoiceRecord}
                        onTextInput={handlePostCaptureTextInput}
                        onClose={handlePostCaptureClose}
                    />
                ) : null
            })()}

            {/* Voice Recording Modal */}
            {isVoiceRecordingOpen && currentPhotoTempId !== null && (() => {
                const currentPhoto = capturedPhotos.find(p => p.tempId === currentPhotoTempId)
                return currentPhoto ? (
                    <VoiceRecordingModal
                        isOpen={isVoiceRecordingOpen}
                        onClose={() => {
                            setIsVoiceRecordingOpen(false)
                            isVoiceRecordingOpenRef.current = false
                            if (currentPhotoTempId !== null) {
                                const p = capturedPhotos.find(x => x.tempId === currentPhotoTempId)
                                handlePostCaptureClose(p?.takenAt ?? new Date().toISOString())
                            }
                        }}
                        onSave={handleVoiceRecordingSave}
                        autoStart={true}
                        imageUrl={currentPhoto.url}
                    />
                ) : null
            })()}

            {/* Text Input Modal */}
            {isTextInputOpen && currentPhotoTempId !== null && (() => {
                const currentPhoto = capturedPhotos.find(p => p.tempId === currentPhotoTempId)
                return currentPhoto ? (
                    <TextInputModal
                        isOpen={isTextInputOpen}
                        onClose={() => {
                            if (!isTextInputSavingRef.current) {
                                setIsTextInputOpen(false)
                                isTextInputOpenRef.current = false
                                if (currentPhotoTempId !== null) {
                                    const p = capturedPhotos.find(x => x.tempId === currentPhotoTempId)
                                    handlePostCaptureClose(p?.takenAt ?? new Date().toISOString())
                                }
                            }
                        }}
                        onSave={handleTextInputSave}
                        onSavingChange={(s) => { isTextInputSavingRef.current = s }}
                        imageUrl={currentPhoto.url}
                    />
                ) : null
            })()}

            {/* Sticky Save Bar - shown for all roles (including Volunteers) when tools/supplies or other editable fields have unsaved changes */}
            <StickySaveBar
                isVisible={hasUnsavedChanges || showSuccessBar}
                isSuccess={showSuccessBar}
                isSubmitting={isSaving}
                onSave={handleSaveAllChanges}
            />

            {/* Tool Kit Contents - Print Only (Separate Page) */}
            {workOrder?.toolsNeeded && Array.isArray(workOrder.toolsNeeded) && workOrder.toolsNeeded.some((item: any) => isToolKit(typeof item === 'string' ? item : item?.name || '')) && (
                <div 
                    className="hidden print:block"
                    style={{
                        pageBreakBefore: 'always',
                        breakBefore: 'page',
                        pageBreakInside: 'avoid',
                        breakInside: 'avoid',
                    }}
                >
                    <div className="space-y-2 py-2 text-[9px] leading-tight">
                        <div className="border-b-2 border-gray-800 pb-1">
                            <h2 className="text-base font-bold leading-none">Tool Kit Contents</h2>
                            <p className="mt-0.5 text-[8px] text-gray-600">Work Order #{workOrder.id}</p>
                        </div>
                        
                        <div className="space-y-2">
                            {TOOL_KIT_CONTENTS.map((section, sectionIndex) => (
                                <div
                                    key={sectionIndex}
                                    className="space-y-1"
                                    style={{ pageBreakInside: 'avoid', breakInside: 'avoid' }}
                                >
                                    <h3 className="border-b border-gray-300 pb-0.5 text-[10px] font-semibold leading-tight">
                                        {section.title}
                                    </h3>
                                    <div className="grid grid-cols-2 gap-x-8 gap-y-0.5">
                                        {section.items.map((item, itemIndex) => (
                                            <div key={itemIndex} className="flex justify-between border-b border-gray-200 pb-0.5">
                                                <span className="text-gray-900">{item.name}</span>
                                                <span className="text-gray-700 font-medium">{item.quantity}</span>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            )}

            {/* Notes/Status/Next Steps Section - For Volunteers, Team Leaders, Base Leaders, and Admins */}
            {canAddNotes && (
                <div className="border-t border-gray-200 pt-2 pb-2 print:hidden">
                    <h3
                        className="text-lg font-semibold mb-2 print:mb-2"
                        style={{ color: "#003764" }}
                    >
                        Notes/Status/Next Steps
                    </h3>
                    <div>
                        <Label htmlFor="workOrderNote" className="text-sm font-medium text-gray-700">
                            Add a note about work done. This will be added to Home Details Project General Notes.
                        </Label>
                        <Textarea
                            id="workOrderNote"
                            placeholder="Notes Status Next Steps"
                            rows={6}
                            value={workOrderNote}
                            onChange={(e) => setWorkOrderNote(e.target.value)}
                            className="mt-1 resize-none"
                        />
                    </div>
                </div>
            )}
            <ScrollToTopFab className="print:hidden" />
        </DashboardContentWrapper>
    )
}

export default WorkOrderDetailPage
